import { useOpenPageInNewTab } from 'src/app/AppAuth/usePageNavigation'
import React, { useId } from 'react'
import log from 'loglevel'
import styled from 'styled-components/macro'
import palette from 'src/designSystem/variables/palette'
import { LinkIcon } from 'src/components/Icons/FileType'

import { getReceptorTemplate } from 'src/xgenerated'
import {
  Tooltip,
  TOOLTIP_TRIGGERS,
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { TextButton } from '../Buttons/TextButton'
import { ClipboardButton } from '../Buttons/ClipboardButton/ClipboardButton'
import { detailsPreview, parseDetailsRows } from './evidence.details'

export const ViewInSourceButton = ({
  receptorModelId,
  link,
  shortName,
  style,
}: {
  receptorModelId: string
  link: string
  shortName?: string
  style?: React.CSSProperties
}): JSX.Element => {
  const openInNewTab = useOpenPageInNewTab()

  let receptorName = 'Source'

  if (shortName) {
    receptorName = shortName
  } else {
    try {
      const receptorTemplate = getReceptorTemplate(receptorModelId)
      receptorName = receptorTemplate?.name || 'Source'
    } catch (e) {
      if (receptorModelId.startsWith('trr-')) {
        log.error('Error getting receptor template', e)
      } else {
        log.info(
          'Invalid receptor model id. Possibly a user uploaded document with the same caption as a linked document.',
          e,
        )
      }
    }
  }

  return (
    <TextButton
      pageDestination={link}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        openInNewTab(link)
      }}
      style={style}
    >
      {`View in ${receptorName}`}
      <LinkIcon />
    </TextButton>
  )
}

const DetailsPreview = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${palette.blue['400']};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

const DetailsTable = styled.table`
  border-collapse: collapse;
  font-size: 12px;

  th,
  td {
    text-align: left;
    padding: 2px 8px 2px 0;
    vertical-align: top;
  }

  th {
    font-weight: 600;
    white-space: nowrap;
    padding-right: 12px;
  }
`

// True when a cell value carries the detail-column contract above. Used to
// decide, per column, whether cells render as a hover table — no receptor or
// evidence caption is hard-coded, so any receptor emitting the shape gets the
// table for free.
export const isDetailsValue = (value?: string): boolean =>
  parseDetailsRows(value) !== null

// Renders an evidence grid cell whose value is a Details JSON payload as a
// short text preview with a real hover table of its label/value pairs. Falls
// back to plain text if the value isn't in the expected shape.
export const DetailsCell = ({ value }: { value?: string }): JSX.Element => {
  const rows = parseDetailsRows(value)
  if (!rows) {
    return <>{value}</>
  }
  const preview = detailsPreview(rows)
  return (
    <Tooltip
      id="evidence-details-cell-tooltip"
      overlayType={TooltipOverlayType.popover}
      placement={TooltipPositions.top}
      tooltipBody={
        <DetailsTable>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th>{row.label}</th>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </DetailsTable>
      }
    >
      {/* Focusable so the tooltip's built-in focus trigger reveals the full
          detail table for keyboard users — the preview text is ellipsized and
          there is no other way to reach the rest of it without a mouse. */}
      <DetailsPreview tabIndex={0}>{preview}</DetailsPreview>
    </Tooltip>
  )
}

const TextCellWrapper = styled.div`
  width: 100%;
  min-width: 0;
  text-align: center;
`

const FullTextBody = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  word-break: break-word;

  /* White copy icon to match the popover text; dims slightly on hover. */
  button svg path {
    fill: ${palette.neutral.white};
  }
  button:hover svg path {
    fill: ${palette.neutral[300]};
  }
`

// Boolean cells render as these marks (see toStringValue); nothing to copy.
const BOOLEAN_MARKS = new Set(['\u2714', '-'])

// Renders a plain evidence grid cell. Hovering shows the full text in a
// popover with a copy button, so any value can be copied; the popover stays
// open while the pointer is over it so the button is reachable. Replaces the
// grid's native title tooltip, whose delay the browser controls. Hover only:
// keepOpenOnHover closes on mouse leave, so a focus trigger would leave the
// popover pinned after tabbing away, and a tab stop per cell would bypass the
// grid's own arrow-key navigation.
export const TextCell = ({ value }: { value?: string }): JSX.Element => {
  const tooltipId = useId()
  if (!value || BOOLEAN_MARKS.has(value)) {
    return <>{value}</>
  }
  return (
    <TextCellWrapper>
      <Tooltip
        id={`evidence-text-cell-tooltip-${tooltipId}`}
        overlayType={TooltipOverlayType.popover}
        placement={TooltipPositions.top}
        trigger={TOOLTIP_TRIGGERS.HOVER}
        keepOpenOnHover
        tooltipBody={
          <FullTextBody>
            <span>{value}</span>
            <ClipboardButton text={value} />
          </FullTextBody>
        }
      >
        <DetailsPreview>{value}</DetailsPreview>
      </Tooltip>
    </TextCellWrapper>
  )
}
